/**
 * Context Capture Utilities
 * Captures page context for chatbot
 */

import { ChatContext } from '../types';
import { extractApplicationContext, FlexitApplicationContext } from './contextExtractor';
import { capturePageAsMarkdown, getPageContentSize } from './pageContentExtractor';
import { RootState } from '../../../reducers';
import { maskSensitiveValue } from './sanitization';

/**
 * Determine current user action based on URL
 */
export const getCurrentUserAction = (): string => {
  const path = window.location.pathname.toLowerCase();
  
  if (path.includes('enrollment')) return 'viewing enrollment';
  if (path.includes('beneficiar')) return 'managing beneficiaries';
  if (path.includes('profile')) return 'viewing profile';
  if (path.includes('claims')) return 'viewing claims';
  if (path.includes('coverage')) return 'viewing coverage';
  if (path.includes('dependent')) return 'managing dependents';
  if (path.includes('payroll')) return 'viewing payroll';
  if (path.includes('settings')) return 'managing settings';
  
  return 'browsing';
};

/**
 * Capture complete page context
 * @param reduxState - Optional Redux state for enhanced context
 * @param includePageContent - Whether to include page content as Markdown (default: true)
 */
export const capturePageContext = (
  reduxState?: RootState,
  includePageContent = true
): ChatContext => {
  // Extract application context from Redux if available
  let appContext: FlexitApplicationContext | undefined;
  if (reduxState) {
    try {
      appContext = extractApplicationContext(reduxState);
    } catch (error) {
      console.warn('Failed to extract application context:', error);
    }
  }
  
  // Capture page content as Markdown
  let pageContent: string | undefined;
  if (includePageContent) {
    try {
      pageContent = capturePageAsMarkdown();
      
      // Log page content size in development mode
      if (process.env.NODE_ENV === 'development' && pageContent) {
        const size = getPageContentSize(pageContent);
        console.log('[ChatBot] Page content captured:', {
          characters: size.characters,
          bytes: size.bytes,
          lines: size.lines
        });
      }
    } catch (error) {
      console.warn('Failed to capture page content:', error);
    }
  }
  
  // Capture validation errors
  const validationErrors = captureValidationErrors();
  
  // Capture modal state
  const modalState = captureModalState();
  
  // Capture loading state
  const isLoading = captureLoadingState();
  
  // Capture user selections
  const userSelections = captureUserSelections();
  
  return {
    pageUrl: window.location.pathname,
    pageTitle: document.title,
    userAction: getCurrentUserAction(),
    additionalData: {
      currentRoute: window.location.pathname,
      pageContent: pageContent,
      applicationContext: appContext,
      validationErrors: validationErrors.length > 0 ? validationErrors : undefined,
      modalState: modalState,
      isLoading: isLoading,
      userSelections: userSelections.length > 0 ? userSelections : undefined,
      timestamp: new Date().toISOString(),
    },
  };
};

/**
 * Capture loading state from the current page
 */
export const captureLoadingState = (): boolean => {
  try {
    // Check for common loading indicators
    const loadingSelectors = [
      '.spinner',
      '.loading-spinner',
      '.loader',
      '.loading',
      '.loading-overlay',
      '[class*="spinner"]',
      '[class*="loading"]'
    ];
    
    for (const selector of loadingSelectors) {
      const element = document.querySelector(selector) as HTMLElement;
      if (element && element.offsetParent !== null) {
        return true;
      }
    }
    
    return false;
  } catch (error) {
    console.warn('Failed to capture loading state:', error);
    return false;
  }
};

/**
 * Check if user is on login/logout page
 */
export const isLoginPage = (): boolean => {
  try {
    const path = window.location.pathname.toLowerCase();
    return path.includes('login') || path.includes('logout') || 
           !!document.querySelector('input[type="password"]');
  } catch (error) {
    console.warn('Failed to check login page:', error);
    return false;
  }
};

/**
 * Capture user selections on the current page
 */
export const captureUserSelections = (): Array<{type: string; label?: string; value: string}> => {
  const selections: Array<{type: string; label?: string; value: string}> = [];
  
  try {
    // Capture selected radio buttons
    const selectedRadios = document.querySelectorAll('input[type="radio"]:checked');
    selectedRadios.forEach(radio => {
      const input = radio as HTMLInputElement;
      const label = input.labels?.[0]?.textContent?.trim() ||
                   input.closest('label')?.textContent?.trim() ||
                   input.getAttribute('aria-label') ||
                   input.name;
      
      if (input.value) {
        selections.push({
          type: 'radio',
          label: label,
          value: input.value
        });
      }
    });
    
    // Capture checked checkboxes
    const selectedCheckboxes = document.querySelectorAll('input[type="checkbox"]:checked');
    selectedCheckboxes.forEach(checkbox => {
      const input = checkbox as HTMLInputElement;
      const label = input.labels?.[0]?.textContent?.trim() ||
                   input.closest('label')?.textContent?.trim() ||
                   input.getAttribute('aria-label') ||
                   input.name;
      
      selections.push({
        type: 'checkbox',
        label: label,
        value: input.value || 'checked'
      });
    });
    
    // Capture selected dropdown options
    const selects = document.querySelectorAll('select');
    selects.forEach(select => {
      const selectedOption = select.options[select.selectedIndex];
      if (selectedOption && selectedOption.value) {
        const label = select.labels?.[0]?.textContent?.trim() ||
                     select.getAttribute('aria-label') ||
                     select.name;
        
        selections.push({
          type: 'select',
          label: label,
          value: selectedOption.text || selectedOption.value
        });
      }
    });
    
    // Capture filled text inputs (excluding password fields)
    const textInputs = document.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"], input[type="number"], input[type="date"]');
    textInputs.forEach(input => {
      const inputEl = input as HTMLInputElement;
      if (inputEl.value && inputEl.value.trim()) {
        const label = inputEl.labels?.[0]?.textContent?.trim() ||
                     inputEl.closest('label')?.textContent?.trim() ||
                     inputEl.getAttribute('aria-label') ||
                     inputEl.placeholder ||
                     inputEl.name;
        
        // Sanitize sensitive data
        const isSensitive = ['ssn', 'sin', 'social', 'card', 'account', 'email', 'password', 'tel', 'phone', 'mobile', 'dob', 'date'].some(
          keyword => (label?.toLowerCase() || '').includes(keyword)
        );

        const typeSensitive = ['email', 'tel', 'number', 'date'].includes(inputEl.type);
        const shouldMaskValue = isSensitive || typeSensitive;
        const normalizedValue = inputEl.value.substring(0, 50);
        
        selections.push({
          type: 'input',
          label: label,
          value: shouldMaskValue ? maskSensitiveValue(normalizedValue) : normalizedValue // Limit length
        });
      }
    });
    
    return selections;
  } catch (error) {
    console.warn('Failed to capture user selections:', error);
    return [];
  }
};

/**
 * Capture modal state from the current page
 */
export const captureModalState = (): {isOpen: boolean; title?: string; content?: string} | undefined => {
  try {
    // Check for open modals
    const modal = document.querySelector('.modal.show, [role="dialog"]') as HTMLElement;
    
    if (!modal) {
      return undefined;
    }
    
    // Check if modal is actually visible
    const computedStyle = window.getComputedStyle(modal);
    if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden') {
      return undefined;
    }
    
    // Extract modal information
    const title = modal.querySelector('.modal-title, .modal-header h1, .modal-header h2, .modal-header h3, .modal-header h4, .modal-header h5')?.textContent?.trim();
    const body = modal.querySelector('.modal-body')?.textContent?.trim();
    
    return {
      isOpen: true,
      title: title,
      content: body
    };
  } catch (error) {
    console.warn('Failed to capture modal state:', error);
    return undefined;
  }
};

/**
 * Capture validation errors from the current page
 */
export const captureValidationErrors = (): Array<{message: string; field?: string}> => {
  const errors: Array<{message: string; field?: string}> = [];
  
  try {
    // Find error messages (only actual errors, not info/warning messages)
    const errorSelectors = [
      '.invalid-feedback',
      '.error-message',
      '.text-danger',
      '.alert-danger',
      '.is-invalid'
    ];
    
    errorSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        const text = el.textContent?.trim();
        const htmlEl = el as HTMLElement;
        // Only capture visible, non-empty error messages
        if (text && text.length > 0 && htmlEl.offsetParent !== null) {
          // Try to find associated field label
          const fieldLabel = el.closest('label')?.textContent?.trim() ||
                           el.previousElementSibling?.textContent?.trim() ||
                           el.parentElement?.querySelector('label')?.textContent?.trim();
          
          errors.push({
            message: text,
            field: fieldLabel
          });
        }
      });
    });
    
    // Remove duplicates
    const uniqueErrors = errors.filter((error, index, self) =>
      index === self.findIndex(e => e.message === error.message)
    );
    
    return uniqueErrors;
  } catch (error) {
    console.warn('Failed to capture validation errors:', error);
    return [];
  }
};

/**
 * Sanitize sensitive data from objects
 */
export const sanitizeData = (data: any): any => {
  if (!data || typeof data !== 'object') return data;
  
  const sensitiveKeys = ['password', 'token', 'apiKey', 'secret', 'ssn', 'sin'];
  const sanitized = { ...data };
  
  Object.keys(sanitized).forEach(key => {
    if (sensitiveKeys.some(sensitive => key.toLowerCase().includes(sensitive))) {
      sanitized[key] = '***';
    } else if (typeof sanitized[key] === 'object') {
      sanitized[key] = sanitizeData(sanitized[key]);
    }
  });
  
  return sanitized;
};
